import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CountryISO,
  NgxIntlTelInputModule,
  PhoneNumberFormat,
  SearchCountryField,
} from 'ngx-intl-tel-input-gg';
import { ConfirmationService, MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadHandlerEvent, FileUploadModule } from 'primeng/fileupload';
import { ImageModule } from 'primeng/image';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { catchError, concatMap, last, lastValueFrom, of, switchMap, tap } from 'rxjs';
import { Clinic } from 'src/app/models/clinic.model';
import { DateMinutes } from 'src/app/models/date-duration.model';
import { Day } from 'src/app/models/day.model';
import { PhoneNumber } from 'src/app/models/phone.model';
import { Specialist, WeekDays } from 'src/app/models/specialist.model';
import { ClinicService } from 'src/app/services/clinic.service';
import { SpecialistService } from 'src/app/services/specialist.service';
import {
  DATE_MINUTES,
  WEEK_DAYS,
  findDateMinutesByCode,
  generatePreview,
  removeCountryCode,
  validateImage,
} from 'src/app/services/utils.service';
import { dateValidator } from 'src/app/validators/date.validator';
import { endAtAfterStartAtValidator } from 'src/app/validators/endAt-after-startAt.validator';

@Component({
  selector: 'specialists-table',
  standalone: true,
  imports: [
    TableModule,
    ImageModule,
    ButtonModule,
    AvatarModule,
    CommonModule,
    DialogModule,
    DividerModule,
    CurrencyPipe,
    ReactiveFormsModule,
    InputTextModule,
    ToastModule,
    InputTextareaModule,
    FileUploadModule,
    DropdownModule,
    ConfirmDialogModule,
    CalendarModule,
    ChipModule,
    NgxIntlTelInputModule,
  ],
  templateUrl: './specialist-table.component.html',
  providers: [MessageService, ConfirmationService],
})
export class SpecialistTableComponent implements OnInit {
  @Input()
  data: Specialist[];
  @Input()
  global: boolean = false;

  @Input()
  clinic: Clinic | null;

  appName: string = localStorage.getItem('appName');
  cols: any[];
  specialist: Specialist;
  isExpanded: boolean = false;
  viewModal: boolean = false;
  addModal: boolean = false;
  editModal: boolean = false;
  editModalNested: boolean = false;
  deleteModal: boolean = false;
  loading: boolean = false;
  form: FormGroup;
  formNested: FormGroup;
  specialistsToAdd: Specialist[];
  selectedSpecialists: Specialist[] = [];

  selectedFile: File | null;
  previewUrl: string | ArrayBuffer | null = null;

  days: Day[] | undefined;
  dateMinutes: DateMinutes[] | undefined;
  diary: { [key: string]: { start: string; end: string }[] } = {};

  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;
  onlyCountries: CountryISO[] = [CountryISO.Spain];

  private fb = inject(FormBuilder);
  private specialistService = inject(SpecialistService);
  private clinicService = inject(ClinicService);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private storage = inject(AngularFireStorage);

  private isInitialTimeSetStartAt = false;
  private isInitialTimeSetEndAt = false;

  constructor() {
    this.form = this.fb.group({
      name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      curriculum: ['', [Validators.required]],
      phoneNumber: [null as PhoneNumber | null, [Validators.required]],
      specialty: ['', [Validators.required]],
      dateDuration: [null as DateMinutes | null, [Validators.required]],
      datePrice: ['', [Validators.required]],
      fileImage: new FormControl(null, [validateImage]),
    });

    this.formNested = this.fb.group(
      {
        day: [null as Day | null, [Validators.required]],
        startAt: [null as Date | null, [Validators.required, dateValidator()]],
        endAt: [null as Date | null, [Validators.required, dateValidator()]],
      },
      { validators: endAtAfterStartAtValidator('startAt', 'endAt') },
    );
  }

  ngOnInit(): void {
    this.reloadSpecialistToAdd();
    this.days = WEEK_DAYS;
    this.dateMinutes = DATE_MINUTES;
    if (this.appName === 'Caser MIMAvet') {
      this.form.value.datePrice = '0';
    }
  }

  reloadSpecialistToAdd() {
    if (!this.global) {
      console.log('entro');
      this.clinicService
        .findBySlug(this.clinic.slug)
        .pipe(
          switchMap((clinic) => {
            this.clinic = { ...clinic };
            return this.specialistService.findSpecialists();
          }),
        )
        .subscribe((specialists) => {
          this.specialistsToAdd = specialists.filter(
            (specialist) => !this.clinic.specialists?.includes(specialist.id),
          );
        });
      this.selectedSpecialists = [];
    }
  }

  view(specialist: Specialist) {
    this.specialist = { ...specialist };
    this.viewModal = true;
  }

  getDiaryDays(diary: { [key: string]: any }): string[] {
    const weekDaysOrder = [
      'monday',
      'tuesday',
      'wednesday',
      'thursday',
      'friday',
      'saturday',
      'sunday',
    ];
    return Object.keys(diary).sort((a, b) => weekDaysOrder.indexOf(a) - weekDaysOrder.indexOf(b));
  }

  getDayName(dayKey: string): string {
    return WeekDays[dayKey];
  }

  isDiaryEmpty(): boolean {
    return Object.keys(this.diary).length === 0;
  }

  openModalEdit(specialist: Specialist) {
    this.specialist = { ...specialist };
    if (this.specialist.diary) {
      this.diary = { ...this.specialist.diary };
    }
    this.previewUrl = '';
    this.form.patchValue({
      name: specialist.name,
      email: specialist.email,
      curriculum: specialist.curriculum,
      phoneNumber: removeCountryCode(specialist.phoneNumber),
      specialty: specialist.specialty,
      dateDuration: findDateMinutesByCode(specialist.dateDuration),
      datePrice: +specialist.datePrice,
    });
    this.editModal = true;
  }

  openModalDelete(item: Specialist) {
    this.deleteModal = true;
    this.specialist = { ...item };
  }

  onDeleteSpecialist(specialist: Specialist) {
    this.confirmationService.confirm({
      key: 'onDeleteSpecialist',
      message: `¿Está seguro que desea eliminar a ${specialist.name}?`,
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-text',
      acceptIcon: 'none',
      rejectIcon: 'none',
      acceptLabel: 'Si',
      rejectLabel: 'No',
      accept: () => {
        this.specialistService.deleteSpecialist(specialist);
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Especialista borrado',
          life: 3000,
        });
      },
      reject: () => {},
    });
  }

  confirmDelete() {
    this.loading = true;
    this.clinicService.removeSpecialist(this.clinic.id, this.specialist.id).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Successful',
          detail: 'Especialista borrado',
          life: 3000,
        });
        this.deleteModal = false;
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        console.error('Error al crear ', error);
      },
    });
  }

  add() {
    this.addModal = true;
  }

  addSchedule() {
    this.editModalNested = true;
  }

  onAddSchedule() {
    this.formNested.markAllAsTouched();
    if (!this.formNested.valid) {
      return;
    }
    const val = this.formNested.value;
    const day = val.day.code;
    const start = this.formatTime(val.startAt);
    const end = this.formatTime(val.endAt);

    if (!this.diary[day]) {
      this.diary[day] = [];
    }
    this.diary[day].push({ start, end });
    this.formNested.reset();
    this.isInitialTimeSetStartAt = false;
    this.isInitialTimeSetEndAt = false;
  }

  onRemoveSchedule(day: string, time: { start: string; end: string }) {
    this.diary[day] = this.diary[day].filter(
      (t) => !(t.start === time.start && t.end === time.end),
    );
    if (this.diary[day].length === 0) {
      delete this.diary[day];
    }
  }

  setInitialTimeStartAt() {
    if (!this.isInitialTimeSetStartAt) {
      const now = new Date();
      now.setMinutes(0, 0, 0);
      this.formNested.get('startAt').setValue(now);
      this.isInitialTimeSetStartAt = true;
    }
  }

  setInitialTimeEndAt() {
    if (!this.isInitialTimeSetEndAt) {
      const now = new Date();
      now.setMinutes(0, 0, 0);
      this.formNested.get('endAt').setValue(now);
      this.isInitialTimeSetEndAt = true;
    }
  }

  resetFormNested() {
    this.formNested.reset();
    this.editModalNested = false;
    this.isInitialTimeSetStartAt = false;
    this.isInitialTimeSetEndAt = false;
  }

  async save() {
    this.form.markAllAsTouched();
    console.log(this.form.value);
    if (!this.form.valid) {
      return;
    }
    this.loading = true;
    const changes = this.form.value as Partial<Specialist>;
    changes.phoneNumber = this.form.value.phoneNumber.e164Number;
    changes.dateDuration = this.form.value.dateDuration.code;

    if (!this.isDiaryEmpty()) {
      changes.diary = this.diary;
    } else {
      changes.diary = null;
    }

    if (this.selectedFile) {
      const filePath = `specialist/${this.specialist.id}/profile`;
      const task = this.storage.upload(filePath, this.selectedFile);
      await lastValueFrom(
        task.snapshotChanges().pipe(
          last(),
          concatMap(() => this.storage.ref(filePath).getDownloadURL()),
          tap((url) => (this.previewUrl = url)),
          catchError((err) => {
            console.log(err);
            alert('dio error');
            return of(null);
          }),
        ),
      );
      changes.imageUrl = this.previewUrl as string;
      this.updateSpecialist(changes);
      return;
    }
    this.updateSpecialist(changes);
  }

  addToClinic() {
    this.loading = true;
    if (this.selectedSpecialists.length === 0) {
      this.loading = false;
      this.addModal = false;
      return;
    }
    const selectedSpecialistIds = this.selectedSpecialists.map((specialist) => specialist.id);
    this.clinicService.addSpecialists(this.clinic.id, selectedSpecialistIds).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Especialistas agregados',
          life: 3000,
        });
        this.addModal = false;
        this.loading = false;
        this.reloadSpecialistToAdd();
      },
      error: (error) => {
        this.loading = false;
        console.error('Error al crear ', error);
      },
    });
  }

  onGlobalFilter(table: Table, event: Event) {
    table.filterGlobal((event.target as HTMLInputElement).value, 'contains');
  }

  onUpload(event: FileUploadHandlerEvent) {
    this.form.patchValue({ fileImage: event.files[0] });
    this.selectedFile = event.files[0];
    generatePreview(this.selectedFile).subscribe({
      next: (previewUrl) => {
        this.previewUrl = previewUrl;
      },
    });
  }

  private updateSpecialist(changes: any) {
    this.specialistService
      .update(this.specialist.id, changes)
      .pipe(
        tap(() => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Especialista actualizado',
            life: 3000,
          });
          this.editModal = false;
          this.loading = false;
          this.form.reset();
        }),
        catchError((error) => {
          this.loading = false;
          console.error('Error al crear ', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  private formatTime(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  }
}
